'use client';

import { FC, useEffect, useState, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useWebChat, useWebChatContent } from '@fshr/hooks';
import { useTealium } from '@fshr/context/tealium-context';
import { getConfig } from '@fshr/shared-react';
import { GlobalStore } from '@fshr/stores';
import { FSLocale, getIsoCodeFromLocale } from '@fshr/shared';
import { Oj } from '@fshr/shared/types';
import { isEmpty } from 'lodash/fp';
import WebChatSignInModal from './webchat-sign-in-dialog';

const MOBILE_MODAL_BREAKPOINT_WIDTH = 700;
const UPCOMING_TRIP_HOST_ROUTE_SEGMENTS = ['/upcoming-trip/', '/profile/my-stays/'];

const shouldCollapseIframeForUpcomingTripViews = () => {
	if (typeof window === 'undefined') return false;

	return (
		window.innerWidth <= MOBILE_MODAL_BREAKPOINT_WIDTH &&
		UPCOMING_TRIP_HOST_ROUTE_SEGMENTS.some(routeSegment =>
			window.location.pathname.includes(routeSegment)
		)
	);
};

const collapseWebMessengerContainer = () => {
	if (!shouldCollapseIframeForUpcomingTripViews()) return;

	const iframe = document.getElementById('web-messenger-container') as HTMLIFrameElement | null;
	if (!iframe) return;

	iframe.style.width = '0px';
	iframe.style.height = '0px';
};

interface WebChatButtonProps {
	propertyCode?: string;
	locale: FSLocale;
	globalStore: GlobalStore;
	booking?: Oj.Booking;
	onSignInRequest?: () => void;
	onReadyWebChat?: () => void;
}

export const WebChatButton: FC<WebChatButtonProps> = observer(
	({ propertyCode, locale, globalStore, booking, onSignInRequest, onReadyWebChat }) => {
		const isServer = typeof window === 'undefined';
		const { webChatContent, bookingData, isLoading } = useWebChatContent(
			locale,
			propertyCode,
			!booking ? globalStore?.activeBookingId : booking?.bookingId
		);
		const { trackInteraction } = useTealium();
		const { NEXT_PUBLIC_IMAGE_BASE_URL } = getConfig();

		const handleReady = () => {
			collapseWebMessengerContainer();
			onReadyWebChat?.();
		};

		const { enabled, openWebChat } = useWebChat({
			webChatContent,
			profile: globalStore?.profile,
			bookingId: (booking as Oj.Booking)?.bookingId || globalStore?.activeBookingId,
			ojBooking: booking as Oj.Booking,
			bffBooking: bookingData,
			localeIsoCode: getIsoCodeFromLocale(locale),
			options: {
				hideFloatingChatButton: true,
				onReady: handleReady,
			},
		});

		const [showChatSignInDialog, setShowChatSignInDialog] = useState(false);

		const hasUserData =
			!globalStore?.profile?.dataRedacted &&
			(!isEmpty(globalStore?.profile) || !isEmpty(booking) || !isEmpty(bookingData));
		const webChatToken =
			globalStore?.profile?.webChatToken ||
			booking?.customers?.[0]?.webChatToken ||
			bookingData?.customer?.webChatToken;

		const isAnonymousChatAllowed = webChatContent?.allowAnonymous;

		const shouldShowWebChat = useMemo(() => {
			// Server-side: don't show
			if (isServer) return false;
			// Still loading: don't show
			if (isLoading || globalStore?.fetching) return false;
			// Smooch not ready: don't show
			if (!enabled) return false;
			// No user data and anonymous not allowed: don't show
			if (!hasUserData && !isAnonymousChatAllowed) return false;
			// Has user data but no webChatToken: don't show
			if (hasUserData && !webChatToken) return false;

			return true;
		}, [
			isServer,
			isLoading,
			globalStore?.fetching,
			enabled,
			hasUserData,
			isAnonymousChatAllowed,
			webChatToken,
		]);

		useEffect(() => {
			if (shouldShowWebChat && openWebChat) {
				window.addEventListener('webchat.open', openWebChat);
				globalStore?.setWebChatEnabled(true);
			} else {
				globalStore?.setWebChatEnabled(false);
			}

			return () => {
				window.removeEventListener('webchat.open', openWebChat);
				globalStore?.setWebChatEnabled(false);
			};
		}, [shouldShowWebChat, openWebChat]);

		if (!shouldShowWebChat) {
			return null;
		}

		// Dialog handlers
		const handleChatButtonClick = () => {
			if (!hasUserData && isAnonymousChatAllowed) {
				setShowChatSignInDialog(true);
			} else {
				openWebChat();
			}

			trackInteraction('chat icon', 'web chat', 'chat start');
		};

		const handleSignIn = () => {
			setShowChatSignInDialog(false);
			onSignInRequest?.();

			trackInteraction('chat icon', 'web chat', 'sign in/register');
		};

		const handleContinueAnonymous = () => {
			setShowChatSignInDialog(false);
			openWebChat();

			trackInteraction('chat icon', 'web chat', 'skip sign in');
		};

		return (
			<>
				{!showChatSignInDialog && (
					<div
						className="fixed bottom-400 w-[50px] h-[50px] right-[110px] cursor-pointer z-99 flex items-center justify-center p-[1px] rounded-full bg-white shadow-[0_2px_8px_rgba(0,0,0,0.15)] duration-500 ease-in-out transition-opacity opacity-100 hover:opacity-50 focus:outline-2 focus:outline-blue-500 focus:outline-offset-2"
						id="webchat-button"
						role="button"
						tabIndex={0}
						aria-label="Chat with us"
						onClick={handleChatButtonClick}
					>
						{webChatContent?.propertyInfo?.chatIcon && (
							<img
								src={`${NEXT_PUBLIC_IMAGE_BASE_URL}${webChatContent.propertyInfo.chatIcon}`}
								alt="Chat icon"
								className="w-[50px] h-[50px] object-contain bg-white rounded-full"
							/>
						)}
					</div>
				)}
				{showChatSignInDialog && (
					<div className="fixed bottom-0 right-0 md:!bottom-[40px] md:right-[95px] w-full max-w-[415px] md:w-[415px] p-150 z-99">
						<WebChatSignInModal
							onSignIn={handleSignIn}
							onContinueAnonymous={handleContinueAnonymous}
						/>
					</div>
				)}
			</>
		);
	}
);

export default WebChatButton;
